const MARKER_RETRY_DELAY_MS = 300

const delay = async (milliseconds: number) => {
    if (milliseconds <= 0) return
    await new Promise<void>((resolve) => setTimeout(resolve, milliseconds))
}

/** Treats missing/timeout/throttle/5xx statuses as ambiguous: the Clerk commit may have landed. */
export const isAmbiguousRequestError = (error: unknown) => {
    if (typeof error !== 'object' || error === null) return true

    const directStatus = 'status' in error ? (error as { status?: unknown }).status : undefined
    const response = 'response' in error ? (error as { response?: unknown }).response : undefined
    const responseStatus =
        typeof response === 'object' && response !== null && 'status' in response
            ? (response as { status?: unknown }).status
            : undefined
    const status = typeof directStatus === 'number' ? directStatus : responseStatus

    return typeof status !== 'number' || status === 408 || status === 425 || status === 429 || status >= 500
}

interface IRejectedMembershipRequestCommit {
    id: string
    organizationId: string
    // Clerk's live Frontend API currently returns `rejected`; older installed SDK types and
    // documentation call the same terminal state `revoked`. Accept both exact terminal values.
    status: 'rejected' | 'revoked'
}

function isExactRejectedRequest(
    result: unknown,
    membershipRequestId: string,
    clerkOrganizationId: string
): result is IRejectedMembershipRequestCommit {
    if (typeof result !== 'object' || result === null) return false
    const candidate = result as { id?: unknown; organizationId?: unknown; status?: unknown }
    return (
        candidate.id === membershipRequestId &&
        candidate.organizationId === clerkOrganizationId &&
        (candidate.status === 'rejected' || candidate.status === 'revoked')
    )
}

/** Refreshes best-effort and reports an outcome whose Clerk commit could not be proven. */
async function unconfirmedOutcome(clerkError: unknown, refreshRequests: () => Promise<unknown>) {
    try {
        await refreshRequests()
        return { status: 'unconfirmed' as const, clerkError }
    } catch (refreshError) {
        return { status: 'unconfirmed_and_refresh_failed' as const, clerkError, refreshError }
    }
}

/**
 * Records requester-visible metadata only when Clerk returns the exact rejected request resource,
 * either from `reject()` or from reloading that exact resource. All unproven outcomes remain
 * unconfirmed and unmarked. Both Clerk terminal status spellings are normalized here.
 */
export async function rejectJoinRequestWithMarker({
    membershipRequestId,
    clerkOrganizationId,
    rejectInClerk,
    readRejectedInClerk,
    recordMarker,
    refreshRequests,
    markerRetryDelayMs = MARKER_RETRY_DELAY_MS,
}: {
    membershipRequestId: string
    clerkOrganizationId: string
    rejectInClerk: () => Promise<unknown>
    /** Exact request-resource reload from Clerk's Frontend API; never a pending-list inference. */
    readRejectedInClerk?: () => Promise<unknown>
    recordMarker: (rejectedRequest: IRejectedMembershipRequestCommit) => Promise<unknown>
    refreshRequests: () => Promise<unknown>
    markerRetryDelayMs?: number
}) {
    let rejectedRequest: unknown
    try {
        rejectedRequest = await rejectInClerk()
    } catch (clerkError) {
        try {
            rejectedRequest = await readRejectedInClerk?.()
        } catch {
            rejectedRequest = null
        }

        if (!isExactRejectedRequest(rejectedRequest, membershipRequestId, clerkOrganizationId)) {
            if (!isAmbiguousRequestError(clerkError)) throw clerkError

            return await unconfirmedOutcome(clerkError, refreshRequests)
        }
    }

    if (!isExactRejectedRequest(rejectedRequest, membershipRequestId, clerkOrganizationId)) {
        const malformedResponseError = new Error('Clerk did not return the exact rejected membership request')
        try {
            rejectedRequest = await readRejectedInClerk?.()
        } catch {
            rejectedRequest = null
        }
        if (!isExactRejectedRequest(rejectedRequest, membershipRequestId, clerkOrganizationId)) {
            return await unconfirmedOutcome(malformedResponseError, refreshRequests)
        }
    }

    let markerError: unknown
    try {
        await recordMarker(rejectedRequest)
    } catch (error) {
        if (isAmbiguousRequestError(error)) {
            await delay(markerRetryDelayMs)
            try {
                await recordMarker(rejectedRequest)
            } catch (retryError) {
                markerError = retryError
            }
        } else {
            markerError = error
        }
    }

    let refreshError: unknown
    try {
        // The exact Clerk rejection is already committed, so refresh even when both marker
        // attempts fail; otherwise the admin sees a stale actionable row.
        await refreshRequests()
    } catch (error) {
        refreshError = error
    }

    if (markerError && refreshError) {
        return { status: 'committed_marker_and_refresh_failed' as const, markerError, refreshError }
    }
    if (markerError) return { status: 'committed_marker_failed' as const, markerError }
    if (refreshError) return { status: 'committed_refresh_failed' as const, refreshError }
    return { status: 'completed' as const }
}

/** Returns an honest success, unknown, or partial-success toast for the Clerk rejection attempt. */
export function getJoinRequestRejectionToast(outcome: Awaited<ReturnType<typeof rejectJoinRequestWithMarker>>) {
    switch (outcome.status) {
        case 'completed':
            return { kind: 'success' as const, message: 'Request rejected' }
        case 'unconfirmed':
            return {
                kind: 'warning' as const,
                message: "We couldn't confirm whether the request was rejected. Reload the page before trying again.",
            }
        case 'unconfirmed_and_refresh_failed':
            return {
                kind: 'warning' as const,
                message:
                    "We couldn't confirm whether the request was rejected, and this list could not refresh. Reload the page before trying again.",
            }
        case 'committed_marker_failed':
            return {
                kind: 'warning' as const,
                message: "Request rejected, but the requester's status could not be updated. Please contact support.",
            }
        case 'committed_refresh_failed':
            return {
                kind: 'warning' as const,
                message: 'Request rejected, but this list could not refresh. Reload the page to see the latest state.',
            }
        case 'committed_marker_and_refresh_failed':
            return {
                kind: 'warning' as const,
                message:
                    "Request rejected, but the requester's status and this list could not be updated. Reload the page and contact support.",
            }
    }
}
